/**
 * TanStack Query hooks for Onboarding Packages — GH-237.
 *
 * All hooks call the /api/onboarding-packages endpoint surface
 * introduced in P3. The API is tenant-scoped server-side; no tenant
 * param is needed from the client (cookie session carries tenant context).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import i18n from 'i18next'
import { apiClient } from '../../lib/api-client'
import type {
  OnboardingPackage,
  OnboardingPackageBatchLink,
  OnboardingPackageLifecycle,
  SourceGroup,
} from '@agentwiki/shared'
import { COMPLETENESS_QUERY_KEY } from './completeness/use-package-completeness'

// ── Response shape types ──────────────────────────────────────────────────────

export interface ListPackagesResponse {
  /** API returns `items` — matches the route handler response shape in P3 */
  items: OnboardingPackage[]
  total: number
  limit: number
  offset: number
}

/**
 * @deprecated Use `OnboardingPackageBatchLink` from `@agentwiki/shared` directly —
 * `sourceGroup` is now part of the canonical shared type. Alias kept for transition.
 */
export type OnboardingPackageBatchLinkWithTag = OnboardingPackageBatchLink

export interface ListBatchLinksResponse {
  /** API returns `batches` — matches the route handler response shape in P3 */
  batches: OnboardingPackageBatchLink[]
  total: number
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const PACKAGES_QUERY_KEYS = {
  all: ['onboarding-packages'] as const,
  list: (params?: { lifecycle?: OnboardingPackageLifecycle; limit?: number; offset?: number }) =>
    ['onboarding-packages', 'list', params] as const,
  detail: (id: string) => ['onboarding-packages', 'detail', id] as const,
  batches: (packageId: string) => ['onboarding-packages', 'batches', packageId] as const,
}

// ── List packages ─────────────────────────────────────────────────────────────

export interface UseOnboardingPackagesParams {
  lifecycle?: OnboardingPackageLifecycle
  limit?: number
  offset?: number
}

export function useOnboardingPackages(
  params: UseOnboardingPackagesParams = {},
  options: { enabled?: boolean } = {},
) {
  // M6 fix: align default with API route default (20); was 50 causing a mismatch
  const { lifecycle, limit = 20, offset = 0 } = params
  const searchParams = new URLSearchParams()
  searchParams.set('limit', String(limit))
  searchParams.set('offset', String(offset))
  if (lifecycle) searchParams.set('lifecycle', lifecycle)

  return useQuery<ListPackagesResponse>({
    queryKey: PACKAGES_QUERY_KEYS.list(params),
    queryFn: () =>
      apiClient.get<ListPackagesResponse>(`/api/onboarding-packages?${searchParams}`),
    enabled: options.enabled ?? true,
  })
}

// ── Single package ────────────────────────────────────────────────────────────

export function useOnboardingPackage(id: string) {
  return useQuery<OnboardingPackage>({
    queryKey: PACKAGES_QUERY_KEYS.detail(id),
    queryFn: () => apiClient.get<OnboardingPackage>(`/api/onboarding-packages/${id}`),
    enabled: Boolean(id),
  })
}

// ── Batches linked to a package ───────────────────────────────────────────────

export function usePackageBatches(packageId: string) {
  return useQuery<ListBatchLinksResponse>({
    queryKey: PACKAGES_QUERY_KEYS.batches(packageId),
    queryFn: () =>
      apiClient.get<ListBatchLinksResponse>(`/api/onboarding-packages/${packageId}/batches`),
    enabled: Boolean(packageId),
  })
}

// OnboardingPackageBatchLinkWithTag is already exported above via the interface declaration

// ── Create package ────────────────────────────────────────────────────────────

export interface CreatePackageInput {
  name: string
  businessPurpose?: string | null
  criticality?: OnboardingPackage['criticality']
  sourceSystems?: string[]
  expectedSourceGroups?: string[]
  /** Server requires owner_user_id_required when null. Default to current user. */
  ownerUserId?: string
}

export function useCreateOnboardingPackage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePackageInput) =>
      apiClient.post<OnboardingPackage>('/api/onboarding-packages', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PACKAGES_QUERY_KEYS.all })
    },
  })
}

// ── Update package (fields + lifecycle transitions) ───────────────────────────

export interface UpdatePackageInput {
  name?: string
  businessPurpose?: string | null
  criticality?: OnboardingPackage['criticality']
  sourceSystems?: string[]
  expectedSourceGroups?: string[]
  lifecycle?: OnboardingPackageLifecycle
  blockedReason?: string | null
}

export function useUpdateOnboardingPackage(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdatePackageInput) =>
      apiClient.patch<OnboardingPackage>(`/api/onboarding-packages/${id}`, input),
    onSuccess: (updated) => {
      // Optimistically update the detail cache immediately
      qc.setQueryData<OnboardingPackage>(PACKAGES_QUERY_KEYS.detail(id), updated)
      void qc.invalidateQueries({ queryKey: PACKAGES_QUERY_KEYS.all })
    },
  })
}

// ── Sample pack — seed/remove demo template packages (GH-268) ─────────────────

export interface CreateSamplePackResponse {
  packages: OnboardingPackage[]
}

export interface RemoveSamplePackResponse {
  removed: number
}

/**
 * useCreateSamplePack — POST /api/onboarding-packages/sample-pack
 *
 * Seeds 3 demo template packages (`isTemplate=true`) for the current tenant.
 * Server-side is idempotent at the per-tenant level (caller still receives
 * the existing templates if any). On success, invalidates the packages list
 * so the new templates appear immediately.
 */
export function useCreateSamplePack() {
  const qc = useQueryClient()
  return useMutation<CreateSamplePackResponse, Error, void>({
    mutationFn: () =>
      apiClient.post<CreateSamplePackResponse>('/api/onboarding-packages/sample-pack'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PACKAGES_QUERY_KEYS.all })
    },
  })
}

/**
 * useRemoveSamplePack — DELETE /api/onboarding-packages/sample-pack
 *
 * Removes ALL `isTemplate=true` packages for the tenant. Confirms via
 * `window.confirm` before firing because templates can look like real data
 * once a user has clicked around. Idempotent on the server (returns
 * `{ removed: 0 }` if nothing matched).
 */
export function useRemoveSamplePack() {
  const qc = useQueryClient()
  return useMutation<RemoveSamplePackResponse, Error, { skipConfirm?: boolean; count?: number } | void>({
    mutationFn: async (input) => {
      const opts = (input as { skipConfirm?: boolean; count?: number } | undefined) ?? {}
      const skipConfirm = opts.skipConfirm ?? false
      const count = opts.count ?? 0
      if (!skipConfirm) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          i18n.t('samplePack.removeConfirm', { ns: 'packages', count }),
        )
        if (!ok) {
          // Throw a sentinel error so the caller can distinguish cancellation.
          const err = new Error('cancelled')
          ;(err as { cancelled?: boolean }).cancelled = true
          throw err
        }
      }
      return apiClient.delete<RemoveSamplePackResponse>('/api/onboarding-packages/sample-pack')
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PACKAGES_QUERY_KEYS.all })
    },
  })
}

// ── Tag batch source-group (GH-235 P5) ────────────────────────────────────────

export interface TagBatchSourceGroupInput {
  /** The source group to assign; null removes the tag (treated as 'other' server-side). */
  sourceGroup: SourceGroup | null
}

/**
 * useTagBatchSourceGroup — PATCH /api/onboarding-packages/:id/batches/:batchId/source-group
 *
 * On success, invalidates both the batches list and the completeness query so
 * the dashboard refreshes automatically.
 *
 * Mutation key is scoped per [packageId, batchId] so concurrent tags on
 * different rows are independent (no serialization across rows).
 */
export function useTagBatchSourceGroup(packageId: string, batchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['onboarding-packages', 'tag-source-group', packageId, batchId],
    mutationFn: (input: TagBatchSourceGroupInput) =>
      apiClient.patch<{ ok: true }>(
        `/api/onboarding-packages/${packageId}/batches/${batchId}/source-group`,
        input,
      ),
    onSuccess: () => {
      // Refresh batches list so displayed tag reflects truth
      void qc.invalidateQueries({ queryKey: PACKAGES_QUERY_KEYS.batches(packageId) })
      // Refresh completeness so dashboard score updates
      void qc.invalidateQueries({ queryKey: COMPLETENESS_QUERY_KEY(packageId) })
    },
  })
}
